import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { White_logo } from "@/lib/Images";
import { logout } from "@/store/slices";

// Icons
import { Menu, X, LayoutDashboard } from "lucide-react";

// Image icons for navigation items
import dashboardIcon from "../assets/images/icon/dashboard_icon.svg";
import tenantIcon from "../assets/images/icon/tenant.svg";
import templatesIcon from "../assets/images/icon/templates.svg";
import analyticsIcon from "../assets/images/icon/analytics.svg";
import settingIcon from "../assets/images/icon/settings_icon.svg";

/** Sidebar nav config */
const navigationItems = [
  { name: "Dashboard", href: "dashboard", icon: dashboardIcon },
  {
    name: "Tenants",
    href: "tenants",
    icon: tenantIcon,
  },
  { name: "Analytics", href: "analytics", icon: analyticsIcon },
  {
    name: "Simulation Templates",
    href: "simulation-templates",
    icon: templatesIcon,
  },
  { name: "Admin Settings", href: "settings", icon: settingIcon },
];

const Sidebar = ({ isHidden = false }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  const toggleMobileMenu = () => setIsMobileMenuOpen((v) => !v);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);
  if (isHidden) return null;
  return (
    <>
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button
          variant="outline"
          size="icon"
          onClick={toggleMobileMenu}
          className="bg-brand-100 shadow-md"
          aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={isMobileMenuOpen}
          aria-controls="app-sidebar"
        >
          {isMobileMenuOpen ? (
            <X className="h-4 w-4 text-neutral-100" />
          ) : (
            <Menu className="h-4 w-4 text-neutral-100" />
          )}
        </Button>
      </div>

      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-brand-100 bg-opacity-50 z-40"
          role="presentation"
          onClick={closeMobileMenu}
        />
      )}

      {/* Sidebar container */}
      <div
        id="app-sidebar"
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 bg-[#172A3F] text-neutral-100 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0",
          isMobileMenuOpen ? "translate-x-0" : "-translate-x-full",
        )}
        role="complementary"
        aria-label="Sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo section */}
          <div className="flex items-center h-28 px-4 border-white/10">
            <img
              src={White_logo}
              alt="RealTimeID Logo"
              className="h-16 w-auto"
            />
          </div>

          {/* Navigation links */}
          <nav className="flex-1 px-4 py-6 space-y-2 mt-3 text-responsive-sm">
            {navigationItems.map((item) => {
              const isActive = location.pathname.split("/").includes(item.href);

              return (
                <Link
                  key={item.name}
                  to={`/${item.href}`}
                  onClick={closeMobileMenu}
                  className={cn(
                    "flex items-center px-4 py-3 font-medium rounded-sm transition-colors duration-200",
                    isActive
                      ? "bg-sidebar-active text-neutral-100"
                      : "text-neutral-100/70 hover:bg-sidebar-hover hover:text-neutral-100",
                  )}
                >
                  <img src={item.icon} alt="" className="mr-3 h-6 w-6" />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* Optional logout section */}

          {/* Sidebar footer */}
          <div className="p-6 border-t border-white/10">
            <p className="text-sm text-neutral-100/50">SimStac Admin v1.0</p>
            <p className="mt-2 text-sm text-neutral-100/50">
              © 2026 SimStac Systems
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
